<script setup lang="ts">
import { usePageMeta } from '@/hooks/usePageMeta';

import BottomNavigation from '@/components/bottomNavigation/BottomNavigation.vue';
import BottomNavigationItem from '@/components/bottomNavigation/BottomNavigationItem.vue';
import SessionTimeout from '@/components/session/SessionTimeout.vue';

import AdviserLine from '@/icons/line/adviser.svg';
import AdviserSolid from '@/icons/solid/adviser.svg';
import HomeLine from '@/icons/line/home.svg';
import HomeSolid from '@/icons/solid/home.svg';
import ToastManager from '@/components/toastManager/ToastManager.vue';
import UserLine from '@/icons/line/user.svg';
import UserSolid from '@/icons/solid/user.svg';
import File from '@/icons/line/file.svg';
import FileAltSolid from '@/icons/solid/file-alt.svg';

import StudentParentHeaderBg from '@/images/backgrounds/student-parent-header-bg.svg';
import StudentParentHeaderHomeBg from '@/images/backgrounds/student-parent-header-home-bg.svg';

const { current } = useRoute();
const routesStartingWith = (base: string) => current.value?.startsWith(base);
const { currentRoute, baseTitle, headline } = usePageMeta();

const headerOffset = ref(0);
const containerRef = ref<HTMLElement | null>(null);

const isHomePage = computed(() => {
  return (
    currentRoute.value === 'student.dashboard.show' ||
    currentRoute.value === 'parent.dashboard.show' ||
    currentRoute.value === 'student.completed-tasks.show'
  );
});

const isHeaderOffscreen = computed(() => {
  return headerOffset.value <= -206;
});

const computedHeaderBackgroundImage = computed(() => {
  return isHomePage.value ? StudentParentHeaderHomeBg : StudentParentHeaderBg;
});

const navigationMap = [
  { condition: () => isHomePage.value, index: 0 }, // Home
  { condition: () => routesStartingWith('student.submissions'), index: 1 }, // Submissions
  { condition: () => routesStartingWith('student.adviser'), index: 2 }, // Adviser
  { condition: () => routesStartingWith('student.profile'), index: 3 } // Profile
];

const currentTab = computed(() => {
  return navigationMap.find((nav) => nav.condition())?.index ?? 0;
});

function isCurrentTab(index: number) {
  return currentTab.value === index;
}

onMounted(() => {
  containerRef.value?.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  containerRef.value?.removeEventListener('scroll', handleScroll);
});

const handleScroll = (event: Event) => {
  const container = event.target as HTMLElement;
  const scrollTop = container.scrollTop;
  headerOffset.value = -scrollTop * 0.5;
};
</script>

<template>
  <div
    ref="containerRef"
    class="relative flex h-screen flex-col overflow-auto scroll-smooth bg-slate-100 pb-[51px] tall:pb-[67px]"
  >
    <div
      class="fixed inset-x-0 bg-secondary-900"
      :style="{ transform: `translateY(${headerOffset}px)` }"
      :class="{
        'pointer-events-none': isHeaderOffscreen,
        'h-[206px]': isHomePage,
        'h-[104px]': !isHomePage
      }"
    >
      <component
        :is="computedHeaderBackgroundImage"
        class="absolute right-4.5 top-3.5 h-4/5 tall:h-full xs:right-5.5"
      />
    </div>
    <header class="relative z-20 shrink-0 grow-0 overflow-hidden pt-4">
      <div
        class="relative z-10 mx-auto flex max-w-screen-sm flex-col gap-y-5 px-4.5 pb-5 xs:px-5.5"
      >
        <h1 class="text-xl font-bold leading-7 text-white">
          {{ headline || baseTitle }}
        </h1>
        <div
          class="fixed inset-x-0 top-0 bg-secondary-900 px-6 py-4 text-center transition-transform duration-300"
          aria-hidden="true"
          :class="{
            'hidden translate-y-0 tall:block': isHeaderOffscreen,
            '-translate-y-full': !isHeaderOffscreen
          }"
        >
          <span class="text-base font-bold leading-5 text-white">{{ headline || baseTitle }}</span>
        </div>
      </div>
      <div
        class="fixed bottom-0 left-0 right-0 z-10 border-t border-slate-200 bg-white px-4.5 shadow-glow-black xs:px-5.5"
      >
        <div class="mx-auto max-w-screen-sm">
          <BottomNavigation :activeIndex="currentTab">
            <BottomNavigationItem
              :icon="HomeLine"
              :iconActive="HomeSolid"
              :isActive="isCurrentTab(0)"
              :href="route('student.dashboard.show')"
              label="Home"
            />
            <BottomNavigationItem
              :icon="File"
              :iconActive="FileAltSolid"
              :isActive="isCurrentTab(1)"
              :href="route('student.submissions.index')"
              label="Submissions"
            />
            <BottomNavigationItem
              :icon="AdviserLine"
              :iconActive="AdviserSolid"
              :isActive="isCurrentTab(2)"
              :href="route('student.adviser.index')"
              label="Adviser"
            />
            <BottomNavigationItem
              :icon="UserLine"
              :iconActive="UserSolid"
              :isActive="isCurrentTab(3)"
              :href="route('student.profile.show')"
              label="Profile"
            />
          </BottomNavigation>
        </div>
      </div>
    </header>
    <main class="relative z-10 flex grow flex-col">
      <slot />
    </main>
    <SessionTimeout />

    <Teleport to="#toast-manager">
      <div class="fixed bottom-17 z-[100] mx-auto flex w-full justify-center px-8 tall:bottom-21">
        <ToastManager class="w-full max-w-3xl" />
      </div>
    </Teleport>
  </div>
</template>
